import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import WorkOutlineIcon from "@material-ui/icons/WorkOutline";
import CalendarTodayOutlinedIcon from "@material-ui/icons/CalendarTodayOutlined";
import LocationOnOutlinedIcon from "@material-ui/icons/LocationOnOutlined";

const useStyles = makeStyles({
  section: {
    width: "100%",
    minHeight: "100vh",
    boxSizing: "border-box",
    backgroundColor: "#353353",
    padding: "90px 7%",
    overflow: "hidden",
  },

  headingContainer: {
    maxWidth: 1200,
    margin: "0 auto 60px",
  },

  eyebrow: {
    display: "block",
    color: "#ff4f81",
    fontSize: 14,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 3,
    marginBottom: 8,
  },

  heading: {
    position: "relative",
    display: "inline-block",
    color: "#fff",
    fontSize: 45,
    fontWeight: 800,
    margin: 0,

    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      bottom: -12,
      width: 55,
      height: 4,
      borderRadius: 10,
      background: "#ff0055",
    },
  },

  subtitle: {
    maxWidth: 650,
    color: "#aaa9bb",
    fontSize: 15,
    lineHeight: 1.7,
    marginTop: 25,
  },

  timeline: {
    position: "relative",
    maxWidth: 1000,
    margin: "0 auto",

    "&::before": {
      content: '""',
      position: "absolute",
      left: 24,
      top: 10,
      bottom: 10,
      width: 2,
      background:
        "linear-gradient(to bottom, #ff0055, rgba(255,255,255,0.08))",
    },
  },

  experience: {
    position: "relative",
    display: "flex",
    gap: 25,
    marginBottom: 30,
  },

  iconWrapper: {
    position: "relative",
    zIndex: 2,

    width: 50,
    height: 50,
    minWidth: 50,

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "50%",

    background: "#353353",

    border: "2px solid #ff0055",

    color: "#ff4f81",

    boxShadow: "0 0 0 6px rgba(255, 0, 85, 0.08)",
  },

  card: {
    flex: 1,

    padding: "28px 30px",

    borderRadius: 18,

    background: "rgba(255, 255, 255, 0.045)",

    border: "1px solid rgba(255, 255, 255, 0.08)",

    transition: "all 0.3s ease",

    "&:hover": {
      transform: "translateY(-4px)",

      borderColor: "rgba(255, 0, 85, 0.35)",

      background: "rgba(255, 255, 255, 0.065)",

      boxShadow: "0 15px 35px rgba(0, 0, 0, 0.15)",
    },
  },

  top: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 20,

    marginBottom: 8,
  },

  role: {
    color: "#fff",
    fontSize: 21,
    fontWeight: 700,
    margin: 0,
  },

  company: {
    color: "#ff4f81",
    fontSize: 14,
    fontWeight: 600,
    marginTop: 5,
  },

  current: {
    display: "inline-block",

    padding: "5px 11px",

    borderRadius: 20,

    background: "rgba(0, 200, 140, 0.1)",

    color: "#4ddbb0",

    fontSize: 11,
    fontWeight: 700,

    whiteSpace: "nowrap",
  },

  metadata: {
    display: "flex",
    flexWrap: "wrap",
    gap: 18,

    margin: "18px 0",

    color: "#88879b",

    fontSize: 12,
  },

  metadataItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },

  metadataIcon: {
    fontSize: 15,
    color: "#ff4f81",
  },

  description: {
    color: "#aaa9bb",
    fontSize: 14,
    lineHeight: 1.75,

    margin: "0 0 18px",
  },

  skills: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },

  skill: {
    padding: "6px 11px",

    borderRadius: 20,

    background: "rgba(255, 255, 255, 0.06)",

    border: "1px solid rgba(255, 255, 255, 0.06)",

    color: "#c5c4d1",

    fontSize: 11,
    fontWeight: 600,
  },

  "@media (max-width: 650px)": {
    section: {
      padding: "70px 20px",
    },

    headingContainer: {
      marginBottom: 45,
    },

    heading: {
      fontSize: 36,
    },

    timeline: {
      "&::before": {
        left: '19px',
      },
    },

    experience: {
      gap: 15,
    },

    iconWrapper: {
      width: 40,
      height: 40,
      minWidth: 40,
    },

    card: {
      padding: "22px 20px",
    },

    top: {
      flexDirection: "column",
      gap: 8,
    },

    role: {
      fontSize: 18,
    },

    metadata: {
      gap: 10,
      flexDirection: "column",
    },
  },
});

const experiences = [
  {
    role: "Software Developer",
    company: "Contour Software",
    type: "Full-time",
    period: "Nov 2024 – Present",
    duration: "1 yr 10 mos",
    location: "Islamabad, Pakistan · Hybrid",
    current: true,

    description:
      "Developing and maintaining enterprise web applications with a strong focus on frontend architecture, reusable components, API integration, and scalable business solutions.",

    skills: [
      "Vue.js",
      "Vuex",
      "JavaScript",
      "TypeScript",
      "REST APIs",
      "UI/UX",
      "Enterprise Applications",
    ],
  },

  {
    role: "Software Engineer",
    company: "PLC Group",
    type: "Full-time",
    period: "May 2024 – Oct 2024",
    duration: "6 mos",
    location: "Rawalpindi, Punjab, Pakistan",

    description:
      "Worked on modern web applications and frontend solutions, integrating APIs and building responsive interfaces with a focus on maintainability and performance.",

    skills: [
      "GraphQL",
      "HTML5",
      "JavaScript",
      "React",
      "CSS3",
      "REST APIs",
      "Frontend Development",
    ],
  },

  {
    role: "Frontend Developer",
    company: "99 Technologies",
    type: "Full-time",
    period: "May 2023 – Apr 2024",
    duration: "1 yr",
    location: "Islamabad, Pakistan · On-site",

    description:
      "Built responsive and interactive frontend applications with reusable components, API integrations, and modern frontend development practices.",

    skills: [
      "CSS3",
      "REST APIs",
      "React",
      "JavaScript",
      "Redux",
      "Responsive Design",
      "UI Development",
    ],
  },

  {
    role: "Frontend Developer",
    company: "Eazisols",
    type: "Full-time",
    period: "Feb 2022 – Mar 2023",
    duration: "1 yr 2 mos",
    location: "Pakistan",

    description:
      "Developed responsive web interfaces and integrated frontend applications with backend APIs while working with reusable components and modern JavaScript technologies.",

    skills: [
      "REST APIs",
      "Redux",
      "JavaScript",
      "React",
      "CSS3",
      "HTML5",
      "Frontend Development",
    ],
  },
];

export default function Experience() {
  const classes = useStyles();

  return (
    <section id="experience" className={classes.section}>

      {/* Heading */}
      <div className={classes.headingContainer}>
        <span className={classes.eyebrow}>
          My Career
        </span>

        <h1 className={classes.heading}>
          Work Experience
        </h1>

        <p className={classes.subtitle}>
          My professional journey building web applications, enterprise
          solutions, and scalable frontend experiences across different
          teams and products.
        </p>
      </div>

      {/* Timeline */}
      <div className={classes.timeline}>

        {experiences.map((experience, index) => (
          <div className={classes.experience} key={index}>

            {/* Timeline icon */}
            <div className={classes.iconWrapper}>
              <WorkOutlineIcon />
            </div>

            {/* Experience card */}
            <div className={classes.card}>

              <div className={classes.top}>

                <div>
                  <h2 className={classes.role}>
                    {experience.role}
                  </h2>

                  <div className={classes.company}>
                    {experience.company} · {experience.type}
                  </div>
                </div>

                {experience.current && (
                  <span className={classes.current}>
                    Current
                  </span>
                )}

              </div>

              {/* Metadata */}
              <div className={classes.metadata}>

                <span className={classes.metadataItem}>
                  <CalendarTodayOutlinedIcon
                    className={classes.metadataIcon}
                  />

                  {experience.period}
                  {" · "}
                  {experience.duration}
                </span>

                <span className={classes.metadataItem}>
                  <LocationOnOutlinedIcon
                    className={classes.metadataIcon}
                  />

                  {experience.location}
                </span>

              </div>

              {/* Description */}
              <p className={classes.description}>
                {experience.description}
              </p>

              {/* Skills */}
              <div className={classes.skills}>
                {experience.skills.map((skill) => (
                  <span
                    className={classes.skill}
                    key={skill}
                  >
                    {skill}
                  </span>
                ))}
              </div>

            </div>
          </div>
        ))}

      </div>
    </section>
  );
}